import { useEffect, useState } from "react";
import { authClient } from "../../lib/auth-client";
import { api } from "../../lib/api";

export interface MemberInfo {
  userId: string;
  displayName: string;
  role: "admin" | "member";
  status: "invited" | "active" | "suspended";
}

interface MeResponse {
  userId: string;
  email: string;
  member: MemberInfo | null;
}

export type MemberState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "signed-in"; email: string; member: MemberInfo };

/**
 * Combines the better-auth session (is there a cookie at all?) with our own
 * /api/v1/me (role/status from member_profiles — the authoritative source per
 * PLAN_CLAUDE_CODE.md §8). A session without an active member_profiles row
 * (e.g. suspended) is treated as signed-out, matching the server middleware.
 *
 * "No session" is derived at render time (not via an effect + setState) so the
 * effect below only ever sets state from an async result, never synchronously
 * in its body — see https://react.dev/learn/you-might-not-need-an-effect.
 */
export function useMember(): MemberState {
  const { data: authSession, isPending } = authClient.useSession();
  const [meResult, setMeResult] = useState<MeResponse | null | "loading">("loading");

  useEffect(() => {
    if (!authSession) return;
    let cancelled = false;
    api
      .get<MeResponse>("/api/v1/me")
      .then((res) => {
        if (!cancelled) setMeResult(res);
      })
      .catch(() => {
        if (!cancelled) setMeResult(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authSession]);

  if (isPending) return { status: "loading" };
  if (!authSession) return { status: "signed-out" };
  if (meResult === "loading") return { status: "loading" };
  if (!meResult || !meResult.member) return { status: "signed-out" };
  return { status: "signed-in", email: meResult.email, member: meResult.member };
}
